import type { ProjectInfo } from "@models/data/projectInfo";

/**
 * The three query parameters Unity's `DeepLinksHandlerModel` reads from a
 * shared URL:
 *
 *   `/query?sl={sharedLinkId}&act={projectCustomerId}&prj={projectId}`
 *
 * `act` + `prj` were added together. A link with only `sl` is the legacy
 * shape and is still resolved against the signed-in user's account/catalog.
 */
export interface ShareLinkRequest {
  readonly sharedLinkId: string;
  readonly accountId: string | null;
  readonly projectId: string | null;
}

export interface ShareLinkTarget {
  readonly project: ProjectInfo;
  /** A SceneLink may name one; invite rows deliberately carry an empty value. */
  readonly sceneId: string | null;
  /**
   * Annotation/sketch/measurement links still open their project/scene, but
   * those resource viewers are not ported. This is Unity's NotFound copy for
   * the global toast surface.
   */
  readonly notice: string | null;
}

export type ResolveShareLinkResult =
  | { readonly ok: true; readonly target: ShareLinkTarget }
  | { readonly ok: false; readonly message: string };

export interface IDeepLinksHandlerModel {
  /**
   * Port of `DeepLinksHandlerModel.ProcessDeeplink`: resolve an own-account,
   * cross-account, or legacy shared-link id to a project/scene target.
   *
   * Navigation stays in the ViewModel/router layer; this is a headless read.
   */
  resolveAsync(
    request: ShareLinkRequest,
    signal?: AbortSignal,
  ): Promise<ResolveShareLinkResult>;
}

export const DEEP_LINKS_HANDLER_MODEL = Symbol.for("Campfire.DeepLinksHandlerModel");

/**
 * Parse the share part of either a browser URL/fullPath or a raw query.
 * URLSearchParams performs the percent-decoding Unity's regex parser omitted.
 */
export function parseShareLinkSearch(value: string): ShareLinkRequest | null {
  const queryStart = value.indexOf("?");
  const rawQuery = queryStart >= 0 ? value.slice(queryStart + 1) : value.replace(/^\?/, "");
  const query = new URLSearchParams(rawQuery);
  const sharedLinkId = query.get("sl")?.trim() ?? "";
  if (!sharedLinkId) return null;
  const accountId = query.get("act")?.trim() || null;
  const projectId = query.get("prj")?.trim() || null;
  return { sharedLinkId, accountId, projectId };
}
